/**
 * Incoming call popup overlay — shows when an inbound call arrives.
 * Displays caller info with accept/reject buttons and plays a ringtone.
 */

import { useEffect } from 'react'
import { Phone, PhoneOff } from 'lucide-react'
import type { IncomingCallInfo } from '../../hooks/useVoIPDevice'

interface Props {
  call: IncomingCallInfo
  onAccept: () => void
  onReject: () => void
}

export default function IncomingCallOverlay({ call, onAccept, onReject }: Props) {
  // Play ringtone on mount, stop on unmount
  useEffect(() => {
    // Use Web Audio API to generate a simple ringtone
    const ctx = new AudioContext()
    let intervalId: ReturnType<typeof setInterval> | null = null
    let isPlaying = true

    const playRingTone = () => {
      if (!isPlaying) return
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()
      osc.connect(gain)
      gain.connect(ctx.destination)
      osc.frequency.value = 440
      gain.gain.value = 0.15
      osc.start()
      // Ring for 0.4s, silence for 0.2s, ring for 0.4s
      setTimeout(() => { if (isPlaying) gain.gain.value = 0 }, 400)
      setTimeout(() => { if (isPlaying) gain.gain.value = 0.15 }, 600)
      setTimeout(() => {
        osc.stop()
        osc.disconnect()
        gain.disconnect()
      }, 1000)
    }

    playRingTone()
    intervalId = setInterval(playRingTone, 2000)

    return () => {
      isPlaying = false
      if (intervalId) clearInterval(intervalId)
      ctx.close().catch(() => {})
    }
  }, [])

  // Auto-reject after 30 seconds (Plivo will time out anyway)
  useEffect(() => {
    const timeout = setTimeout(() => {
      onReject()
    }, 30000)
    return () => clearTimeout(timeout)
  }, [onReject])

  const displayNumber = call.callerNumber || 'Unknown'
  const displayName = call.callerName !== 'Unknown Caller' ? call.callerName : null

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-20 pointer-events-none">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/20 pointer-events-auto" />

      {/* Card */}
      <div className="relative pointer-events-auto bg-white rounded-2xl shadow-2xl border border-gray-200 w-80 overflow-hidden animate-[slideDown_0.3s_ease-out]">
        {/* Pulsing green bar */}
        <div className="h-1.5 bg-green-500 animate-pulse" />

        <div className="p-6">
          {/* Caller info */}
          <div className="flex flex-col items-center text-center mb-6">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-3 animate-[pulse_1.5s_ease-in-out_infinite]">
              <Phone size={28} className="text-green-600" />
            </div>
            <p className="text-xs font-medium text-green-600 uppercase tracking-wider mb-2">Incoming Call</p>
            {displayName && (
              <p className="text-lg font-bold text-gray-900">{displayName}</p>
            )}
            <p className={`font-mono ${displayName ? 'text-sm text-gray-500' : 'text-lg font-bold text-gray-900'}`}>
              {displayNumber}
            </p>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-3">
            <button
              onClick={onReject}
              className="flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-red-500 hover:bg-red-600 text-white font-medium transition-colors"
            >
              <PhoneOff size={18} />
              Decline
            </button>
            <button
              onClick={onAccept}
              className="flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-green-500 hover:bg-green-600 text-white font-medium transition-colors"
            >
              <Phone size={18} />
              Accept
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
